// src/pages/Agents/MySQL/MySQLAgents.js
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Stepper, Step, StepLabel, Button, Box, Typography, Card, CardHeader, CardContent, TextField, Alert } from '@mui/material';
import RouterAgentModal from '../RouterAgentModal';

const steps = ['Database Connection Details', 'Agent Backstory', 'Task Description'];

const DEFAULT_MYSQL_BACKSTORY = `You are a seasoned database administrator with deep expertise in SQL. 
You can translate natural language questions into precise SQL queries to retrieve information. 
You can only retrieve information from a 'products' table with columns: id (INT), name (VARCHAR), category (VARCHAR), price (DECIMAL), stock_quantity (INT). 
You only perform read operations (SELECT queries).`;

const DEFAULT_MYSQL_DESCRIPTION = `Your task is to: 
1. Understand the user's question about data (assume it relates to a 'products' table with columns: id, name, category, price, stock_quantity).
2. Formulate a syntactically correct SQL SELECT query to retrieve the relevant information.
3. Execute this query using the 'MySQL Database Query Tool'. The tool expects only the SQL query string as input.
4. Based on the tool's JSON output (which will contain 'status' and 'data' or 'error_message'), formulate a natural language response for the user.
   - If successful and data is found, present the key information clearly.
   - If successful but no data, inform the user.
   - If an error occurred, report the error in a user-friendly way.
Only generate SELECT queries. Example: User asks 'How many laptops?', you might form 'SELECT COUNT(*) FROM products WHERE category = 'Electronics' AND name LIKE '%Laptop%';'.`;

export default function MySQLAgents() {
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({ 
        host: '', 
        user: '', 
        password: '', 
        database: '', 
        backstory: '', 
        description: '' 
    });
    const [isActive, setIsActive] = useState(false);
    const [isConfigured, setIsConfigured] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isRouterModalOpen, setRouterModalOpen] = useState(false);
    const { chatbotId: paramsChatbotId } = useParams();
    const navigate = useNavigate();

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('agents', 'view');
                const modifyPerm = await hasPermission('agents', 'add|edit');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        document.title = 'MySQL Agents | KlonIT Web Application';
    }, []);

    const fetchAgentAndCredentialStatus = useCallback(async () => {
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.get(`/api/agents/status`, { params });
            const mysqlConfig = response.data.agents.MYSQL;
            setIsActive(mysqlConfig.is_active);
            setIsConfigured(mysqlConfig.is_configured);

            if (mysqlConfig.is_configured) {
                const credsResponse = await axiosInstance.get(`/api/agents/mysql/credentials`, { params });
                if (credsResponse.data.success && credsResponse.data.data) {
                    const fetchedData = credsResponse.data.data;
                    setFormData(prev => ({
                        ...prev,
                        ...fetchedData,
                        backstory: fetchedData.backstory || DEFAULT_MYSQL_BACKSTORY,
                        description: fetchedData.description || DEFAULT_MYSQL_DESCRIPTION,
                    }));
                }
            } else {
                setFormData(prev => ({
                    ...prev,
                    backstory: DEFAULT_MYSQL_BACKSTORY,
                    description: DEFAULT_MYSQL_DESCRIPTION,
                }));
            }
        } catch (err) {
            setError("Failed to fetch agent status or credentials.");
            setFormData(prev => ({
                ...prev,
                backstory: DEFAULT_MYSQL_BACKSTORY,
                description: DEFAULT_MYSQL_DESCRIPTION,
            }));
        }
    }, [paramsChatbotId]);

    useEffect(() => {
        if (canView) {
            fetchAgentAndCredentialStatus();
        }
    }, [fetchAgentAndCredentialStatus, canView]);

    const handleToggle = async (newStatus) => {
        if (!canModify) return;

        setIsActive(newStatus);
        setMessage('');
        setError('');
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            await axiosInstance.post(`/api/agents/status`,
                { agent_name: 'MYSQL', is_active: newStatus },
                { params }
            );
            setMessage(`Agent successfully ${newStatus ? 'activated' : 'deactivated'}.`);
        } catch (err) {
            setError('Failed to update agent status.');
            setIsActive(!newStatus);
        }
    };

    const handleSave = async () => {
        setIsProcessing(true);
        setMessage('');
        setError('');
        try {
            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
            const response = await axiosInstance.post(`/api/agents/mysql/credentials`, formData, { params });
            setMessage(response.data.message);
            setIsConfigured(true);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save configuration.");
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>Provide the credentials for your MySQL database. This will allow the agent to connect and query your data.</Typography>
                        <div className="mb-3">
                            <label className="form-label">Database Host</label>
                            <input 
                                type="text" 
                                name="host" 
                                value={formData.host} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="e.g., localhost or 127.0.0.1" 
                                required 
                                disabled={!canModify}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Database Name</label>
                            <input 
                                type="text" 
                                name="database" 
                                value={formData.database} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="e.g., products" 
                                required 
                                disabled={!canModify}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Database User</label>
                            <input 
                                type="text" 
                                name="user" 
                                value={formData.user} 
                                onChange={handleChange} 
                                className="form-control" 
                                placeholder="e.g., user_name" 
                                required 
                                disabled={!canModify}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Database Password</label>
                            <input 
                                type="password" 
                                name="password" 
                                value={formData.password} 
                                onChange={handleChange} 
                                className="form-control" 
                                required 
                                disabled={!canModify}
                            />
                        </div>
                    </Box>
                );
            case 1:
                return (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>Define the agent's personality and background. This helps set the context for its responses. You can change your table and column names with meta data.</Typography>
                        <TextField
                            name="backstory"
                            label="Agent Backstory"
                            multiline
                            rows={6}
                            fullWidth
                            value={formData.backstory}
                            onChange={handleChange}
                            placeholder="e.g., You are a seasoned database administrator..."
                            disabled={!canModify}
                        />
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>Describe the specific task the agent should perform. Be detailed about its function and expected output. You can change your table and column names with meta data.</Typography>
                        <TextField
                            name="description"
                            label="Task Description"
                            multiline
                            rows={8}
                            fullWidth
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="e.g., Your task is to: 1. Understand the user's question about data..."
                            disabled={!canModify}
                        />
                    </Box>
                );
            default:
                return <Typography>Configuration Complete!</Typography>;
        }
    };

    // Show loading state while checking permissions
    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    // Show access denied if no view permission
    if (!canView) {
        return (
            <main className="content">
                <div className="container-fluid p-0">
                    <Alert severity="error" sx={{ mt: 6 }}>
                        <strong>Access Denied</strong>
                        <br />
                        You don't have permission to view MySQL Agent configuration. Please contact your administrator.
                    </Alert>
                </div>
            </main>
        );
    }

    return (
        <main className="content">
            <div className="container-fluid p-0">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h1 className="h3 d-inline align-middle"><strong>MySQL Agent Configuration</strong></h1>
                    <Link to="/agents" className="btn btn-secondary"><i className="fa fa-arrow-left me-2"></i>Back to Agents</Link>
                </div>

                {message && <div className="alert alert-success">{message}</div>}
                {error && <div className="alert alert-danger">{error}</div>}
                {!canModify && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        You have view-only access. Contact your administrator to make changes.
                    </Alert>
                )}

                <Card>
                    <CardHeader
                        title="Agent Status"
                        action={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button variant="outlined" size="small" onClick={() => setRouterModalOpen(true)} disabled={!canModify}>
                                    Edit Router Agent
                                </Button>
                                <div className="form-check form-switch mt-2">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        role="switch" 
                                        checked={isActive} 
                                        onChange={(e) => handleToggle(e.target.checked)} 
                                        disabled={!isConfigured || !canModify} 
                                        id="mysql-agent-switch" 
                                    />
                                    <label className="form-check-label" htmlFor="mysql-agent-switch">
                                        {isActive ? 'Active' : 'Inactive'}
                                    </label>
                                </div>
                            </Box>
                        }
                    />
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {isActive
                                ? "The MySQL agent is active and will be used for database-related queries."
                                : "The agent is inactive. Activate it to allow access to your database."}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        
                        {renderStepContent(activeStep)}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button disabled={activeStep === 0} onClick={handleBack}>Back</Button>
                            {activeStep < steps.length - 1 ? (
                                <Button variant="contained" onClick={handleNext}>Next</Button>
                            ) : (
                                canModify && (
                                    <Button variant="contained" color="primary" onClick={handleSave} disabled={isProcessing}>
                                        {isProcessing ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                )
                            )}
                        </Box>
                    </CardContent>
                </Card>
                <RouterAgentModal open={isRouterModalOpen} onClose={() => setRouterModalOpen(false)} />
            </div>
        </main>
    );
}